import { api } from '@hunk/shared/api';
import type { OrderView } from '@hunk/shared/api/orders';
import { DeliveryMethodTranslated, deliveryNeedsAddress, formatMoney, type OrderStatusType, OrderStatusTranslated, type PaymentOptionType } from '@hunk/shared/common';
import { productPath } from '@hunk/shared/productUrl';
import { CircleCheck, CircleX, Loader2, Package, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router';

import { openPayment, PaymentPicker, usePaymentOptions } from '@/components/PaymentPicker';
import { ProductImage } from '@/components/ProductCard';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { errorText, useSession } from '@/hooks/useSession';
import { cn } from '@/lib/utils';
import { Spinner } from '@/pages/CheckoutPage';

const STATUS_STYLE: Record<OrderStatusType, string> = {
  pending_payment: 'bg-amber-500/15 text-amber-400',
  paid: 'bg-emerald-500/15 text-emerald-400',
  processing: 'bg-sky-500/15 text-sky-400',
  shipped: 'bg-sky-500/15 text-sky-400',
  ready_for_pickup: 'bg-emerald-500/15 text-emerald-400',
  completed: 'bg-neutral-500/20 text-neutral-300',
  canceled: 'bg-red-500/15 text-red-400'
};
export function OrderStatusBadge({ status }: { status: OrderStatusType }) {
  return <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap', STATUS_STYLE[status])}>{OrderStatusTranslated[status]}</span>;
}

/**
 * One order. With ?tx= (back from the payment page) it polls the payment until the provider confirms it.
 * An unpaid order can be paid again from here.
 */
export function OrderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useAppNavigate();
  const { status, authHeaders } = useSession();
  const [order, setOrder] = useState<OrderView>();
  const [error, setError] = useState<string>();
  const [tx, setTx] = useState(params.get('tx') ?? undefined);
  const [txStatus, setTxStatus] = useState<string>();
  const [frameUrl, setFrameUrl] = useState<string | undefined>((location.state as { frameUrl?: string } | null)?.frameUrl);
  const external = (location.state as { external?: boolean } | null)?.external === true;

  const load = useCallback(async () => {
    try {
      const r = (await api.orders.myOrder(Number(id), { headers: authHeaders() })).response;
      setOrder(r.order);
      if (!tx && r.order?.status === 'pending_payment' && r.payment?.status === 'pending') setTx(r.payment.internalTransactionId);
    } catch (err) {
      setError(errorText(err));
    }
  }, [id, authHeaders, tx]);
  useEffect(() => {
    if (status === 'authenticated') void load();
  }, [status, load]);

  // poll the payment (every 4 s, 15 min)
  useEffect(() => {
    if (!tx || status !== 'authenticated') return;
    let stop = false;
    const started = Date.now();
    const tick = async () => {
      try {
        const r = (await api.orders.paymentStatus(tx, { headers: authHeaders() })).response;
        if (stop) return;
        setTxStatus(r.status);
        if (r.status !== 'pending') {
          setFrameUrl(undefined);
          void load();
          return;
        }
      } catch {
        // keep polling
      }
      if (!stop && Date.now() - started < 15 * 60_000) setTimeout(() => void tick(), 4000);
    };
    void tick();
    return () => {
      stop = true;
    };
  }, [tx, status, authHeaders, load]);

  // the provider's return page inside our frame says it is back -> close the frame, polling goes on
  useEffect(() => {
    const onMsg = (e: MessageEvent) => e.origin === window.location.origin && e.data?.hunkPayment && setFrameUrl(undefined);
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  if (status === 'anonymous') return <Navigate to={`/login?next=${encodeURIComponent(`/order/${id}`)}`} replace />;
  if (error) return <p className="py-24 text-center text-red-400">{error}</p>;
  if (!order) return <Spinner />;

  const waiting = order.status === 'pending_payment' && txStatus === 'pending';
  return (
    <main className="mx-auto max-w-3xl px-3 py-6 md:px-4 md:py-10">
      {txStatus === 'success' || (tx && order.paidOn) ? (
        <Banner ok title="התשלום התקבל, תודה!" text={`הזמנה ${order.number} התקבלה ותטופל בהקדם.`} />
      ) : waiting ? (
        <div className="mb-6 flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-neutral-950 p-6 text-center">
          <Loader2 className="text-primary size-10 animate-spin" />
          <p className="font-bold">ממתינים לאישור התשלום...</p>
          <p className="text-sm text-neutral-400">{external ? 'דף התשלום נפתח בדפדפן. סיימו שם וחזרו לכאן - ' : ''}האישור יכול לקחת עד חצי דקה.</p>
        </div>
      ) : tx && (txStatus === 'failed' || txStatus === 'canceled') ? (
        <Banner title="התשלום לא הושלם" text="אפשר לנסות שוב למטה." />
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-extrabold">הזמנה {order.number}</h1>
        <OrderStatusBadge status={order.status} />
        <span className="flex-1" />
        <span className="text-sm text-neutral-400">{new Date(order.createdOn).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}</span>
      </div>

      {order.status === 'pending_payment' && !waiting && <PayAgain order={order} onStarted={(o, r) => openPayment(r, o, navigate)} />}

      <OrderDetails order={order} />
      <Link to="/orders" className="mt-6 inline-block text-sm text-neutral-400 hover:text-white">
        ← כל ההזמנות שלי
      </Link>

      {frameUrl && (
        <div className="fixed inset-0 z-[90] flex flex-col bg-black">
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
            <span className="text-sm font-semibold">תשלום מאובטח</span>
            <button type="button" onClick={() => setFrameUrl(undefined)} className="text-neutral-400 hover:text-white" aria-label="סגירה">
              <X className="size-5" />
            </button>
          </div>
          <iframe src={frameUrl} title="תשלום" allow="payment" className="w-full flex-1 border-0 bg-white" />
        </div>
      )}
    </main>
  );
}

function Banner({ ok, title, text }: { ok?: boolean; title: string; text: string }) {
  return (
    <div
      className={cn('mb-6 flex flex-col items-center gap-2 rounded-2xl border p-6 text-center', ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5')}
    >
      {ok ? <CircleCheck className="size-14 text-emerald-400" /> : <CircleX className="size-14 text-red-400" />}
      <p className="text-xl font-bold">{title}</p>
      <p className="text-sm text-neutral-400">{text}</p>
    </div>
  );
}

function PayAgain({ order, onStarted }: { order: OrderView; onStarted: (o: PaymentOptionType, r: Awaited<ReturnType<typeof api.orders.payOrder>>['response']) => void }) {
  const { authHeaders, isMiniApp } = useSession();
  const options = usePaymentOptions();
  const [option, setOption] = useState<PaymentOptionType>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const pay = async () => {
    if (!option) return;
    setBusy(true);
    setError(undefined);
    try {
      onStarted(option, (await api.orders.payOrder(order.id, { paymentOption: option, app: isMiniApp ? 'mini' : 'web' }, { headers: authHeaders() })).response);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
      <h2 className="mb-1 font-bold">ההזמנה עוד לא שולמה</h2>
      <p className="mb-3 text-sm text-neutral-400">הזמנה שלא שולמה מבוטלת אוטומטית אחרי 48 שעות.</p>
      {options ? <PaymentPicker options={options} value={option} onChange={setOption} /> : <Spinner small />}
      {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
      <button
        type="button"
        disabled={!option || busy}
        onClick={() => void pay()}
        className="brand-grad brand-grad-hover mt-3 flex w-full items-center justify-center gap-2 rounded-full py-3 font-bold text-white disabled:opacity-50"
      >
        {busy && <Loader2 className="size-5 animate-spin" />}
        לתשלום {formatMoney(order.total)}
      </button>
    </section>
  );
}

export function OrderDetails({ order }: { order: OrderView }) {
  const address = [order.street, order.houseNumber, order.apartment ? `דירה ${order.apartment}` : '', order.city, order.zip].filter(Boolean).join(' ');
  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col divide-y divide-white/10 rounded-2xl border border-white/10 bg-neutral-950">
        {order.items.map((i) => (
          <li key={`${i.productId}-${i.name}`} className="flex items-center gap-3 p-3">
            <span className="size-14 shrink-0 overflow-hidden rounded-lg bg-white p-0.5">
              <ProductImage src={i.image ?? undefined} alt="" />
            </span>
            <span className="min-w-0 flex-1">
              {i.productId ? (
                <Link to={productPath({ id: i.productId, name: i.name })} className="line-clamp-2 font-semibold hover:underline">
                  {i.name}
                </Link>
              ) : (
                <span className="line-clamp-2 font-semibold">{i.name}</span>
              )}
              <span className="text-sm text-neutral-400">
                {formatMoney(i.unitPrice)} × {i.qty}
              </span>
            </span>
            <span className="font-bold whitespace-nowrap">{formatMoney(i.unitPrice * i.qty)}</span>
          </li>
        ))}
        <li className="flex flex-col gap-1 p-3 text-sm">
          <div className="flex justify-between text-neutral-300">
            <span>מוצרים</span>
            <span>{formatMoney(order.itemsTotal)}</span>
          </div>
          <div className="flex justify-between text-neutral-300">
            <span>{order.deliveryMethod === 'pickup' ? 'איסוף עצמי' : 'משלוח'}</span>
            <span>{order.shippingFee ? formatMoney(order.shippingFee) : 'חינם'}</span>
          </div>
          <div className="flex justify-between text-lg font-extrabold">
            <span>סה"כ</span>
            <span>{formatMoney(order.total)}</span>
          </div>
        </li>
      </ul>
      <div className="grid gap-3 rounded-2xl border border-white/10 bg-neutral-950 p-4 text-sm sm:grid-cols-2">
        <div>
          <p className="mb-1 font-bold">{DeliveryMethodTranslated[order.deliveryMethod]}</p>
          {order.deliveryLabel && <p className="text-neutral-300">{order.deliveryLabel}</p>}
          {deliveryNeedsAddress(order.deliveryMethod) && <p className="text-neutral-300">{address}</p>}
          {order.discreet && <p className="mt-1 text-emerald-400">אריזה דיסקרטית</p>}
          {order.trackingNumber && (
            <p className="mt-1 flex items-center gap-1 text-neutral-300">
              <Package className="size-4" />
              מספר מעקב: <span dir="ltr">{order.trackingNumber}</span>
            </p>
          )}
        </div>
        <div className="text-neutral-300">
          <p className="mb-1 font-bold text-white">פרטי הזמנה</p>
          <p>{order.fullName}</p>
          <p dir="ltr" className="text-end">
            {order.phone}
          </p>
          {order.email && (
            <p dir="ltr" className="text-end">
              {order.email}
            </p>
          )}
          {order.notes && <p className="mt-1 text-neutral-400">הערות: {order.notes}</p>}
        </div>
      </div>
    </div>
  );
}
